/**
 * feature/map — spec §F5 (Map Explorer). Route `/`.
 * Composition so far: the Leaflet canvas (§F5.1) + the hierarchy boundary layers from
 * PostGIS (§F5.8/§D2) + the sidebar (§F5.2) + the choropleth (§F5.3) + the legend and its
 * style editor (§F5.6) + the Operational hierarchy widget (§F5.8) + the unit profile
 * (§F5.7) + the search box, time bar and status bar (§F5.10) + the mini indicator dock and
 * its popover (§F5.9) + the map-tool column with its basemap panel, measure/draw/swipe
 * behaviours and §G6 AOI clip (§F5.10) + the §F6 analysis dock.
 *
 * Every floating widget is a child of `MapView` so that `#mapwrap` is its containing block
 * — which is also the box `lib/draggable.ts` clamps against (§F5.11).
 */
import { MapView } from './MapView';
import { ActivityHover } from './ActivityHover';
import { DefaultView } from './DefaultView';
import { HierarchyLayers } from './HierarchyLayers';
import { Choropleth } from './Choropleth';
import { Overlays } from './Overlays';
import { RasterLayers } from './RasterLayers';
import { ReferenceDefaults } from './ReferenceDefaults';
import { ResultLayers } from './ResultLayers';
import { VectorLayers } from './VectorLayers';
import { MapTools } from './MapTools';
import { SearchBox } from './SearchBox';
import { StatusBar } from './StatusBar';
import { EpochSlider } from '../widgets/EpochSlider';
import { Legend } from '../widgets/Legend';
import { OperationalHierarchy } from '../widgets/OperationalHierarchy';
import { TimeBar } from '../widgets/TimeBar';
import { UnitProfile } from '../widgets/UnitProfile';
import { Sidebar, SidebarToggle } from '../sidebar';
import { AnalysisDock } from '../analysis';

export function MapPage() {
  return (
    <div id="view-map" className="view">
      {/* S3: the cold-load default view, and the reference layers the client's table turns
          on. Neither renders anything; both are one write on the arrival of `/meta/config`. */}
      <DefaultView />
      <ReferenceDefaults />
      <Sidebar />
      <MapView>
        {/* Order here is irrelevant to stacking — every layer names its §F5.1 pane — but
            it reads bottom-up on the map: rasters (z350), choropleth (z400), boundaries
            (z420), thematic vectors and §F5.5 overlay symbols (z450), §F6 analysis
            results (z460). */}
        <RasterLayers />
        <Choropleth />
        <HierarchyLayers />
        <VectorLayers />
        <Overlays />
        {/* Client change, 2026-09-23: under the rail's **All** theme, hovering a boundary
            opens a crystal card of that unit's activity categories. This mounts no Leaflet
            object at all — it fetches the counts per hierarchy row and `HierarchyLayers`
            builds the card at `mouseover`. Fetches nothing under any other theme. */}
        <ActivityHover />
        <ResultLayers />
        <SidebarToggle />
        <SearchBox />
        <MapTools />
        <OperationalHierarchy />
        <Legend />
        <TimeBar />
        {/* S3: the series slider, in the same bottom-centre slot. Only one of the two is
            ever on screen — a theme maps a dataset or a variable, not both. */}
        <EpochSlider />
        <UnitProfile />
        {/*
          **The §F5.9 indicator dock and its popover are gone from the explorer** — client
          specification S3. Six round buttons pinned to the map's right edge, each opening a
          panel about a contractual indicator, are exactly the surface the client asked to
          move behind the rail's Indicators icon: "contractual and non-contractual
          indicators live ONLY under that icon; they are not reachable anywhere else in the
          explorer."

          **Removed from this composition, not deleted.** `widgets/IndicatorDock.tsx`,
          `widgets/IndicatorPopover.tsx` and `widgets/dock.ts` are untouched and still
          exported, because the dashboard task is where six quick-view panels about the
          contractual indicators belong and S3 says in its own words that the code paths are
          kept. `state.dockInd` and its two actions stay in the §F1 store for the same
          reason. What changed is that nothing on the map mounts them.
        */}
        {/* §F6. Last of the floating panels because it is the widest and the most recent
            thing the reader asked for; it renders nothing at all while it is closed. */}
        <AnalysisDock />
        <StatusBar />
      </MapView>
    </div>
  );
}

export { MapView };
export default MapPage;
